import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 pb-12 px-4 sm:px-6 lg:px-8 pt-20">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6 sm:p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
        <p className="text-gray-600 mb-8">
          <strong>Effective Date:</strong> 12/17/2024<br />
          <strong>Last Updated:</strong> 12/17/2024
        </p>

        <div className="prose prose-lg max-w-none text-gray-700">
          <p className="mb-6">
            AdroitOne Inc. ("Company," "we," "us," or "our") is committed to protecting the privacy of our clients, candidates, and website users. This Privacy Policy outlines how we collect, use, disclose, and protect your personal information when you interact with us through our website, services, and other communication channels.
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-xl font-medium text-gray-800 mb-3">1.1 Personal Information</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Mailing address</li>
              <li>Job title or position</li>
              <li>Resume, CV, or professional qualifications</li>
              <li>Payment or billing details (if applicable)</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3">1.2 Non-Personal Information</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Browser type and version</li>
              <li>IP address</li>
              <li>Device type</li>
              <li>Cookies and tracking technologies</li>
              <li>Pages visited, time spent on our site, and interaction details</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3">1.3 Sensitive Personal Data</h3>
            <p>
              If required for specific roles, we may collect additional information like social security numbers, identification documents, or background check data. This will always be done with your explicit consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Provide recruitment, staffing, and technology services.</li>
              <li>Process job applications and match candidates with suitable opportunities.</li>
              <li>Communicate with you about services, updates, or job openings.</li>
              <li>Improve our website and services through analytics and feedback.</li>
              <li>Fulfill contractual obligations and comply with legal requirements.</li>
              <li>Customize user experiences and send marketing materials (if opted-in).</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. How We Share Your Information</h2>
            <p>We may share your personal information with:</p>
            
            <h3 className="text-xl font-medium text-gray-800 mb-3">3.1 Third-Party Service Providers</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>To assist in recruitment, web development, and technology services.</li>
              <li>For payment processing, IT support, and marketing analytics.</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3">3.2 Clients</h3>
            <p>To match candidates with suitable roles or share information for recruitment purposes (with consent).</p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">3.3 Legal Obligations</h3>
            <p>If required to comply with applicable laws, regulations, legal processes, or enforceable governmental requests.</p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">3.4 Business Transfers</h3>
            <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Your Privacy Rights</h2>
            <p className="mb-4">Depending on your jurisdiction, you may have the following rights:</p>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> Request corrections to inaccurate or incomplete data.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data, subject to legal and contractual obligations.</li>
              <li><strong>Data Portability:</strong> Request to receive your personal data in a structured, commonly used format.</li>
              <li><strong>Opt-Out:</strong> Opt out of marketing communications or certain data processing activities.</li>
              <li><strong>Restriction:</strong> Restrict certain uses of your data.</li>
            </ul>
            <p>To exercise your rights, contact us at <a href="mailto:admin@adroitone.com" className="text-blue-600 hover:underline">admin@adroitone.com</a>.</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Data Retention</h2>
            <p>We retain your personal data only as long as necessary to fulfill the purposes outlined in this Privacy Policy. This includes retaining data for:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Compliance with legal, tax, or regulatory obligations.</li>
              <li>Resolving disputes and enforcing agreements.</li>
              <li>Ongoing recruitment, staffing, or technology services.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Cookies and Tracking Technologies</h2>
            <p className="mb-4">Our website uses cookies and similar tracking technologies to enhance user experience and analyze website traffic. You can control or disable cookies through your browser settings.</p>
            
            <h3 className="text-xl font-medium text-gray-800 mb-3">Types of Cookies We Use:</h3>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>Essential Cookies:</strong> Necessary for site functionality.</li>
              <li><strong>Performance Cookies:</strong> Collect information about site usage.</li>
              <li><strong>Targeting Cookies:</strong> Used for personalized marketing and advertisements.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Data Security</h2>
            <p>We implement appropriate technical and organizational measures to protect your personal data from unauthorized access, use, disclosure, alteration, or destruction. These measures include encryption, secure servers, firewalls, and regular security audits.</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. International Data Transfers</h2>
            <p>If you access our services from outside the United States, note that your data may be transferred to, stored, and processed in the United States or other countries where we or our third-party service providers operate. By using our services, you consent to such transfers.</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Third-Party Links</h2>
            <p>Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review their privacy policies before submitting personal information.</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Changes to This Privacy Policy</h2>
            <p>We reserve the right to update this Privacy Policy at any time. Any changes will be effective upon posting the updated version on our website. We encourage you to review this Privacy Policy periodically for updates.</p>
          </section>

          <section className="mt-12 p-6 bg-gray-50 rounded-lg border-l-4 border-blue-500">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Contact Us</h2>
            <p className="mb-4">If you have any questions or concerns about this Privacy Policy or how we handle your personal data, please contact us:</p>
            <div className="text-gray-700">
              <p className="font-medium text-lg">AdroitOne Inc.</p>
              <p className="mb-2">Email: <a href="mailto:admin@adroitone.com" className="text-blue-600 hover:underline">admin@adroitone.com</a></p>
              <p>Address: 245 E Gun Hill Road, New York City, NY 10467</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;